import React, { useState, useCallback, useEffect } from "react";
import styled, { withTheme } from "styled-components";
import {
  emailRegex,
  FieldsValidation,
  noDateDefinedOrEmpty,
  NotificationType,
  studiesIds,
} from "../../../modules/constants";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import Checkbox from "../../ui-library/Checkbox/Checkbox";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getCurrentLocale } from "../../../i18n/helpers";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Label = styled.span`
  color: ${(props) => props.theme?.mortar};
  font-size: 15px;
  font-weight: 600;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 48px;
  @media (max-width: 576px) {
    margin-top: auto;
    padding-top: 2rem;
    .btn {
      width: 100%;
    }
  }
`;

const StyledCheckboxLabel = styled(Label)`
  cursor: pointer;
  margin-left: 5px;
`;

const ContactInformationForm = (props) => {
  const {
    onSubmit,
    username,
    email,
    phoneCountry,
    phone,
    yearBirth,
    notifications,
    studyId,
    onClose,
  } = props;

  const { t } = useTranslation();

  const [formValues, setFormValues] = useState({
    ugnxID: "",
    yearBirth: "",
    email: "",
    phoneCountry: "",
    phoneNumber: "",
    dialCode: "",
    notificationEmail: false,
    notificationPhone: false,
  });

  useEffect(() => {
    setFormValues({
      ugnxID: username,
      yearBirth:
        yearBirth && yearBirth !== noDateDefinedOrEmpty
          ? moment(yearBirth).format("Y")
          : "-",
      email: email,
      phoneCountry: phoneCountry || "us",
      phoneNumber: phone,
      notificationEmail: notifications?.indexOf(NotificationType.EMAIL) !== -1,
      notificationPhone: notifications?.indexOf(NotificationType.PHONE) !== -1,
    });
  }, [username, yearBirth, email, phoneCountry, phone, notifications]);

  const [formErrors, setFormError] = useState({
    email: false,
    emailMessage: t("errorText.invalid.email"),
    phone: false,
    phoneMessage: t("errorText.invalid.phoneNumber"),
  });

  const isFormValid = useCallback(() => {
    return !(formErrors.email || formErrors.phone);
  }, [formErrors]);

  const updateFormValues = (index, value, country) => {
    formValues[index] = value;
    if (index === "phoneNumber") {
      formValues["phoneCountry"] = country?.countryCode;
      formValues["dialCode"] = country?.dialCode;
    }
    setFormValues({ ...formValues });
    validateForm(index, value);
  };

  const isEmailValid = (email) => {
    if (
      (email &&
        emailRegex.test(email) &&
        email.length <= FieldsValidation.email.size) ||
      (!email && !formValues?.notificationEmail)
    ) {
      return true;
    }
    return false;
  };

  const isPhoneValid = (phone) => {
    if (phone || (!phone && !formValues.notificationPhone)) {
      const onlyNumber = phone?.replace(formValues?.dialCode, "");

      if (
        (onlyNumber &&
          onlyNumber?.length >= FieldsValidation.phone.minSize &&
          onlyNumber?.length < FieldsValidation.phone.size) ||
        (!onlyNumber && !formValues.notificationPhone)
      ) {
        return true;
      }
    }
    return false;
  };

  const validateForm = (index, value) => {
    switch (index) {
      case "notificationEmail":
        setFormError({
          ...formErrors,
          email: !isEmailValid(formValues?.email),
        });
        break;
      case "email":
        setFormError({
          ...formErrors,
          email: !isEmailValid(value),
        });
        break;
      case "phoneNumber":
        setFormError({
          ...formErrors,
          phone: !isPhoneValid(value),
        });
        break;
      case "notificationPhone":
        setFormError({
          ...formErrors,
          phone: !isPhoneValid(formValues?.phoneNumber),
        });
        break;
      default:
        break;
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      let tempNotifications = [];
      if (formValues.notificationEmail) {
        tempNotifications.push(NotificationType.EMAIL);
      }
      if (formValues.notificationPhone) {
        tempNotifications.push(NotificationType.PHONE);
      }

      const formData = {
        email: formValues.email,
        phoneNumber: formValues.phoneNumber,
        country: formValues.phoneCountry || "us",
        reasonForChange: formValues.reasonForChange,
        notifications: tempNotifications,
      };

      onSubmit(formData);
    }
  };

  return (
    <FormContainer data-testid="contact-information-form">
      <LabelledInput
        title={t("appText.participantID")}
        value={formValues?.ugnxID || ""}
        type="text"
        data-testid="ugnx-id"
        readOnly={true}
      />
      <LabelledInput
        className="mt-3"
        title={t("appText.yearOfBirth")}
        value={formValues?.yearBirth || ""}
        type="text"
        data-testid="year-birth-id"
        readOnly={true}
      />
      {studyId.toLocaleLowerCase() !==
      studiesIds.lcfaod401.toLocaleLowerCase() ? (
        <>
          <div className="mt-4">
            <Label>{t("appText.contactInformation.help")}</Label>
          </div>
          <LabelledInput
            className="mt-3"
            title={t("appText.email")}
            data-testid="email-id"
            value={formValues?.email || ""}
            type="email"
            autoFocus
            hasError={formErrors?.email}
            errorMessage={formErrors?.emailMessage}
            onChange={(e) => updateFormValues("email", e.target.value)}
          />

          <LabelledInput
            className="mt-3"
            title={`${t("appText.phoneNumber")}:`}
            data-testid="phonenumber-id"
            value={formValues?.phoneNumber || ""}
            country={formValues?.phoneCountry || "us"}
            hasError={formErrors?.phone}
            errorMessage={formErrors?.phoneMessage}
            type="tel"
            locale={getCurrentLocale()}
            onChange={(value, country) =>
              updateFormValues("phoneNumber", value, country)
            }
          />
          <div className="mt-4">
            <Label>{`${t("appText.notifications")}:`}</Label>
          </div>
          <span className="mt-2">
            <Checkbox
              data-testid="notification-email-id"
              checked={formValues?.notificationEmail || false}
              onClick={(e) =>
                updateFormValues(
                  "notificationEmail",
                  !formValues?.notificationEmail
                )
              }
              icon={true}
              square={true}
            />

            <StyledCheckboxLabel
              data-testid="notification-email-label"
              onClick={(e) =>
                updateFormValues(
                  "notificationEmail",
                  !formValues?.notificationEmail
                )
              }
            >
              {t("appText.email")}
            </StyledCheckboxLabel>
          </span>
          <span className="mt-3">
            <Checkbox
              data-testid="notification-phone-id"
              checked={formValues?.notificationPhone || false}
              onClick={(e) =>
                updateFormValues(
                  "notificationPhone",
                  !formValues?.notificationPhone
                )
              }
              icon={true}
              square={true}
            />
            <StyledCheckboxLabel
              data-testid="notification-phone-label"
              onClick={(e) =>
                updateFormValues(
                  "notificationPhone",
                  !formValues?.notificationPhone
                )
              }
            >
              {t("appText.phone")}
            </StyledCheckboxLabel>
          </span>

          <ActionButtonsContainer>
            <PrimaryButton
              data-testid="submit-button"
              onClick={handleSubmit}
              disabled={!isFormValid()}
            >
              {t("appText.save")}
            </PrimaryButton>
          </ActionButtonsContainer>
        </>
      ) : (
        <ActionButtonsContainer>
          <PrimaryButton data-testid="submit-button" onClick={onClose}>
            {t("appText.close")}
          </PrimaryButton>
        </ActionButtonsContainer>
      )}
    </FormContainer>
  );
};

export default withTheme(ContactInformationForm);
